/**
 * Phase 550c (Slice 1) — AI Agency Command Center
 * Phase 605 — UI Unification: dark engineering-console aesthetic
 * replaced with the standard Out-Play light-theme card aesthetic
 * (bg-white, border-gray-200, rounded-xl). Data-fetching logic
 * (vitals, kill-switch, polling) unchanged.
 *
 * The 4-team autonomous sales agency control room. Slice 1 ships
 * Team 1 (SDR Scraper) + Team 2 (AE Outreach) live panels. Teams 3
 * (Closer) and 4 (CS) follow, plus Sweep History (Phase 550l).
 */
import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Bot, Activity, Zap, Hourglass, ChevronRight, RefreshCw, Workflow,
  ShieldAlert, ShieldCheck, Settings,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import SOSConfigModal from '@/components/admin/agency/SOSConfigModal';
import SDRHealthBanner from '@/components/admin/agency/SDRHealthBanner';

// Phase 605 — Lazy-mount the heavy team panels. Each panel opens its own
// 60s polling interval and fires an initial fetch on mount; mounting all
// 5 (Teams 1-4 + Sweep History) simultaneously was contributing to peak-
// memory pressure that crashed the Playwright Chromium target. Lazy +
// Suspense lets the page paint progressively and stages the requests.
const TeamOneScraperPanel = lazy(() => import('@/components/admin/agency/TeamOneScraperPanel'));
const TeamTwoOutreachPanel = lazy(() => import('@/components/admin/agency/TeamTwoOutreachPanel'));
const TeamThreeClosePanel = lazy(() => import('@/components/admin/agency/TeamThreeClosePanel'));
const TeamFourCSPanel = lazy(() => import('@/components/admin/agency/TeamFourCSPanel'));
const SweepHistoryPanel = lazy(() => import('@/components/admin/agency/SweepHistoryPanel'));

const PanelFallback = ({ accent = 'gray' }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <Skeleton className={`h-5 w-40 bg-${accent}-100 mb-3`} />
    <Skeleton className="h-3 w-full bg-gray-100 mb-2" />
    <Skeleton className="h-3 w-3/4 bg-gray-100 mb-2" />
    <Skeleton className="h-24 w-full bg-gray-100 mt-3" />
  </div>
);

const POLL_MS = 60_000;

export default function GrowthCommandCenter() {
  const [vitals, setVitals] = useState({
    total_enriched: 0,
    total_pitched: 0,
    loading: true,
  });
  const [killSwitch, setKillSwitch] = useState({
    active: false,
    set_at: null,
    set_by: null,
    loading: true,
    busy: false,
  });
  const [sosOpen, setSosOpen] = useState(false);

  const loadVitals = useCallback(async () => {
    try {
      const [sdr, ae, ks] = await Promise.all([
        api.get('/super-admin/sales-team/sdr/recent-enriched?limit=1'),
        api.get('/super-admin/sales-team/ae-outreach/recent-pitches?limit=1'),
        api.get('/super-admin/agency/kill-switch'),
      ]);
      setVitals({
        total_enriched: sdr.data?.total_enriched ?? 0,
        total_pitched: ae.data?.total_pitched ?? 0,
        loading: false,
      });
      setKillSwitch((s) => ({
        ...s,
        active: !!ks.data?.active,
        set_at: ks.data?.set_at || null,
        set_by: ks.data?.set_by || null,
        loading: false,
      }));
    } catch (e) {
      setVitals((v) => ({ ...v, loading: false }));
      setKillSwitch((s) => ({ ...s, loading: false }));
    }
  }, []);

  const toggleKillSwitch = useCallback(async () => {
    setKillSwitch((s) => ({ ...s, busy: true }));
    try {
      const resp = await api.post('/super-admin/agency/kill-switch/toggle', {
        active: !killSwitch.active,
      });
      setKillSwitch((s) => ({
        ...s,
        active: !!resp.data?.active,
        set_at: resp.data?.set_at || null,
        set_by: resp.data?.set_by || null,
        busy: false,
      }));
      toast.success(
        resp.data?.active
          ? 'AI Agency PAUSED — next SDR + AE cron will short-circuit'
          : 'AI Agency RESUMED — cron sweeps active again',
      );
    } catch (e) {
      setKillSwitch((s) => ({ ...s, busy: false }));
      toast.error(e?.response?.data?.detail || 'Kill-switch toggle failed');
    }
  }, [killSwitch.active]);

  useEffect(() => {
    loadVitals();
    const t = setInterval(loadVitals, POLL_MS);
    return () => clearInterval(t);
  }, [loadVitals]);

  return (
    <div
      className={`min-h-screen text-gray-900 transition-colors duration-500 ${
        killSwitch.active ? 'bg-rose-50/40' : 'bg-gray-50'
      }`}
      data-testid="growth-command-center"
    >
      {/* Phase 611 — Health banner. Renders only on warn/critical, so
          healthy sweeps stay silent. Polls every 60s. */}
      <SDRHealthBanner />

      {/* Global header */}
      <header
        className={`border-b transition-colors duration-500 ${
          killSwitch.active
            ? 'border-rose-200 bg-rose-50/60'
            : 'border-gray-200 bg-white'
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 py-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`rounded-lg border p-2 ${
                  killSwitch.active
                    ? 'border-rose-200 bg-rose-50'
                    : 'border-emerald-200 bg-emerald-50'
                }`}
              >
                <Workflow
                  className={`h-5 w-5 ${
                    killSwitch.active ? 'text-rose-600' : 'text-emerald-600'
                  }`}
                />
              </div>
              <div>
                <h1 className="text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">
                  AI Agency Command Center
                </h1>
                <p className="text-xs text-gray-500">
                  The autonomous sales agency — live telemetry from every
                  team in the pipeline.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {killSwitch.active ? (
                <Badge
                  className="flex items-center gap-2 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-50"
                  data-testid="agency-status-badge"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
                  </span>
                  <span className="font-medium">AI Agency · PAUSED</span>
                </Badge>
              ) : (
                <Badge
                  className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
                  data-testid="agency-status-badge"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  </span>
                  <span className="font-medium">AI Agency · ACTIVE</span>
                </Badge>
              )}

              {/* Master SOS Kill-Switch */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant={killSwitch.active ? 'default' : 'destructive'}
                    size="sm"
                    disabled={killSwitch.busy || killSwitch.loading}
                    className={
                      killSwitch.active
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-rose-600 text-white hover:bg-rose-700'
                    }
                    data-testid="agency-kill-switch-btn"
                  >
                    {killSwitch.active ? (
                      <>
                        <ShieldCheck className="mr-2 h-3.5 w-3.5" />
                        Resume Agency
                      </>
                    ) : (
                      <>
                        <ShieldAlert className="mr-2 h-3.5 w-3.5" />
                        SOS Pause
                      </>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="agency-kill-switch-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {killSwitch.active
                        ? 'Resume the AI Agency?'
                        : 'Pause the entire AI Agency?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {killSwitch.active
                        ? 'The 01:00 IST SDR scrape and 09:45 IST AE pitch sweeps will resume on their next tick. Closer Agent (Team 3) was unaffected.'
                        : 'Both Team 1 (SDR scrape, 01:00 IST) and Team 2 (AE pitch, 09:45 IST) cron sweeps will short-circuit on their next tick — no Places API spend, no LLM tokens, no Meta/Resend dispatch. Closer Agent (Team 3) continues so inbound replies still get triaged. Use this if Meta flags the WhatsApp account or you notice an LLM loop.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="agency-kill-switch-cancel">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={toggleKillSwitch}
                      className={
                        killSwitch.active
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'bg-rose-600 text-white hover:bg-rose-700'
                      }
                      data-testid="agency-kill-switch-confirm"
                    >
                      {killSwitch.active ? 'Yes, resume' : 'Yes, pause everything'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Phase 550k — Escalation Matrix config gear */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSosOpen(true)}
                className="border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                data-testid="agency-sos-config-btn"
                title="Watchdog Escalation Matrix"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={loadVitals}
                className="border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                data-testid="agency-refresh-btn"
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Refresh
              </Button>
            </div>
          </div>

          {killSwitch.active && (
            <div
              className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
              data-testid="agency-paused-banner"
            >
              Agency is paused by{' '}
              <span className="font-mono text-rose-900">
                {killSwitch.set_by || 'super admin'}
              </span>
              . SDR + AE crons will skip their next tick. Hit
              <span className="mx-1 font-medium">Resume Agency</span>
              when ready.
            </div>
          )}

          {/* Global vitals strip */}
          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3">
            <VitalTile
              icon={Bot}
              tone="emerald"
              label="Total Leads Enriched"
              value={vitals.total_enriched}
              loading={vitals.loading}
              dataTestId="vital-total-enriched"
            />
            <VitalTile
              icon={Zap}
              tone="amber"
              label="Total Pitches Dispatched"
              value={vitals.total_pitched}
              loading={vitals.loading}
              dataTestId="vital-total-pitched"
            />
            <VitalTile
              icon={Hourglass}
              tone="cyan"
              label="Next Pipeline Tick"
              valueText={killSwitch.active ? 'PAUSED' : '01:00 IST'}
              hint={killSwitch.active ? 'SOS engaged' : 'SDR scrape · Team 1'}
              loading={false}
              dataTestId="vital-next-tick"
            />
          </div>
        </div>
      </header>

      {/* Two-column flow visualization */}
      <main className="mx-auto max-w-7xl px-6 py-6">
        <div className="mb-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-widest text-gray-400">
          <span className="text-emerald-600">Discovery</span>
          <ChevronRight className="h-3 w-3 text-gray-300" />
          <span className="text-amber-600">Pitch</span>
          <ChevronRight className="h-3 w-3 text-gray-300" />
          <span className="text-rose-600">Close</span>
          <ChevronRight className="h-3 w-3 text-gray-300" />
          <span className="text-cyan-600">Adoption</span>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <Suspense fallback={<PanelFallback accent="emerald" />}>
            <TeamOneScraperPanel />
          </Suspense>
          <Suspense fallback={<PanelFallback accent="amber" />}>
            <TeamTwoOutreachPanel />
          </Suspense>
        </div>

        <div className="mt-5">
          <Suspense fallback={<PanelFallback accent="rose" />}>
            <TeamThreeClosePanel />
          </Suspense>
        </div>

        <div className="mt-5">
          <Suspense fallback={<PanelFallback accent="cyan" />}>
            <TeamFourCSPanel />
          </Suspense>
        </div>

        {/* Phase 550l — unified Sweep History feed */}
        <div className="mt-5">
          <Suspense fallback={<PanelFallback accent="emerald" />}>
            <SweepHistoryPanel />
          </Suspense>
        </div>

        {/* Closing flourish — the 4-team agency is now fully online */}
        <div
          className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/50 p-5 text-center shadow-sm"
          data-testid="agency-fully-online"
        >
          <Activity className="mx-auto mb-2 h-5 w-5 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-700">
            All 4 teams of the Autonomous Sales Agency are online
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Team 1 hunts · Team 2 pitches · Team 3 closes · Team 4
            prevents churn — operating 24/7 with deterministic
            templates and the SOS Kill-Switch one click away.
          </p>
        </div>
      </main>

      {/* Phase 550k — Escalation Matrix config modal (gear icon in header) */}
      <SOSConfigModal
        open={sosOpen}
        onOpenChange={setSosOpen}
        onSaved={loadVitals}
      />
    </div>
  );
}

function VitalTile({ icon: Icon, tone, label, value, valueText, hint, loading, dataTestId }) {
  // Phase 605 light theme — soft tonal accents on white cards.
  const toneStyles = {
    emerald: { iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', valueColor: 'text-emerald-700' },
    amber:   { iconBg: 'bg-amber-50',   iconColor: 'text-amber-600',   valueColor: 'text-amber-700' },
    cyan:    { iconBg: 'bg-cyan-50',    iconColor: 'text-cyan-600',    valueColor: 'text-cyan-700' },
  };
  const t = toneStyles[tone] || toneStyles.emerald;
  return (
    <div
      className="flex items-start justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      data-testid={dataTestId}
    >
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500">
          {label}
        </div>
        {loading ? (
          <Skeleton className="mt-2 h-7 w-16 bg-gray-200" />
        ) : (
          <div className={`mt-1 font-mono text-2xl font-semibold ${t.valueColor}`}>
            {valueText !== undefined ? valueText : (value ?? 0)}
          </div>
        )}
        {hint && (
          <div className="mt-1 text-[11px] text-gray-500">{hint}</div>
        )}
      </div>
      <div className={`rounded-md p-2 ${t.iconBg}`}>
        <Icon className={`h-4 w-4 ${t.iconColor}`} />
      </div>
    </div>
  );
}
